import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';
import pickBy from 'lodash/pickBy';
import range from 'lodash/range';

import { weekDaysMap, weekDaysMapReversed } from '../modules/public/room/services';
import { getTimeSlots, HOURS_MAP_12, HOURS_MAP_24 } from './hours-map';

export const timeSlotsMaxValue = 49;
export const timeFieldPlaceholderValue = -100;

export const allTimeOptionsLocalized = (timeFormat) => {
  return Object.entries(getTimeSlots(timeFormat)).map(([key, value]) => ({
    booked: false,
    id: parseInt(key, 10),
    value,
  }));
};

export const emptyTime = () => ({ value: null, label: '' });

export const createDateWrapper = (date, format = 'dddd Do MMMM YYYY') => ({
  value: date,
  label: window.dayjs(date, 'DD/MM/YYYY').format(format),
});

export const createTimeWrapper = (time, timeFormat = 24) => ({
  value: time,
  label: timeFormat === 12 ? HOURS_MAP_12[time] : HOURS_MAP_24[time],
});

const getDayOfWeek = (date, format = 'DD/MM/YYYY') => {
  if (!date) {
    return null;
  }
  const dayOfWeek = window.dayjs(date, format).day();

  return {
    id: dayOfWeek,
    label: weekDaysMap[dayOfWeek],
  };
};

const calculateEarliestOpeningHour = openingHours => Math.min(...flatten(openingHours));
const calculateFinalClosingHour = openingHours => Math.max(...flatten(openingHours));

const calculateOpeningHoursExtremes = openingHours => ([
  calculateEarliestOpeningHour(openingHours.map(Object.values)),
  calculateFinalClosingHour(openingHours.map(Object.values)),
]);

const extractAllHoursFromTimeRanges = timeRanges => flatMap(timeRanges, ({ timeFrom, timeTo }) => range(timeFrom, timeTo));

export const extractBookedTimeRangesOfDate = (date, partiallyBookedDates) => {
  if (!date) {
    return [];
  }
  const formattedDate = window.dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD');

  return partiallyBookedDates
    ? partiallyBookedDates[formattedDate] || []
    : [];
};

export const checkIfSelectedTimeTouchesBookedTimeRanges = (bookedTimeRanges, timeFrom, timeTo) => bookedTimeRanges.some(bookedTimeRange => {
  const bookedHours = range(bookedTimeRange.timeFrom, bookedTimeRange.timeTo);
  const selectedHours = range(timeFrom, timeTo);

  return intersection(bookedHours, selectedHours).length > 0;
});

export const resolveClosedDayIds = openingHours => {
  const closedDays = Object.keys(pickBy(openingHours, timeRanges => timeRanges.length === 0));

  return closedDays.map(dayName => weekDaysMapReversed[dayName]);
};

export const calculateDifferenceInTime = (timeFrom, timeTo, appendHoursSuffix = true) => {
  const hours = (timeTo - timeFrom) / 2;

  return appendHoursSuffix
    ? hours + (hours > 1 ? ' hrs' : ' hr')
    : hours;
};

const markBookedTimeOptions = bookedHoursOfSelectedDate => timeOption => ({
  ...timeOption,
  booked: bookedHoursOfSelectedDate.indexOf(timeOption.id) !== -1,
});

/**
 * TimeOption is used for AvailabilityTimeInput.
 * See the shape below!
 *
 * @param date
 * @param openingHours
 * @param partiallyBookedDates
 * @param timeFormat
 * @returns {{booked: boolean, id: number, value: *}[]}
 */
export const createTimeOptions = ({ date, openingHours, partiallyBookedDates, timeFormat }) => {
  const bookedTimeRanges = extractBookedTimeRangesOfDate(date, partiallyBookedDates);
  const bookedHoursOfSelectedDate = bookedTimeRanges ? extractAllHoursFromTimeRanges(bookedTimeRanges) : [];

  if (openingHours) {
    const { label: dayOfWeek } = getDayOfWeek(date);
    const [earliestOpeningHour, finalClosingHour] = calculateOpeningHoursExtremes(openingHours[dayOfWeek]);

    return allTimeOptionsLocalized(timeFormat)
      .filter(timeOption => timeOption.id >= earliestOpeningHour && timeOption.id <= finalClosingHour)
      .map(markBookedTimeOptions(bookedHoursOfSelectedDate));
  }

  return allTimeOptionsLocalized(timeFormat).map(markBookedTimeOptions(bookedHoursOfSelectedDate));
};

export const getTimeOptions = (date, roomAvailability, timeFormat) => {
  if (!date) {
    return createTimeOptions({
      date: null,
      openingHours: null,
      timeFormat,
    });
  }
  return createTimeOptions({
    date: date.value,
    openingHours: roomAvailability.openingHours,
    partiallyBookedDates: roomAvailability.partiallyBookedDates,
    timeFormat,
  });
};

export const formatRelativeTimeDifference = (elapsedSeconds) => {
  const absoluteSeconds = Math.abs(elapsedSeconds); // Use absolute value to handle both past and future
  const minutesDiff = Math.round(absoluteSeconds / 60);
  const hoursDiff = Math.round(minutesDiff / 60);
  const daysDiff = Math.round(hoursDiff / 24);
  const monthsDiff = Math.round(daysDiff / 30);

  if (daysDiff >= 60) {
    return `${monthsDiff} months`;
  }

  if (hoursDiff >= 48) {
    return `${daysDiff} days`;
  }

  if (hoursDiff >= 3) {
    return `${hoursDiff} hours`;
  }

  if (minutesDiff >= 60) {
    const fullHours = Math.floor(minutesDiff / 60);
    const remainingMinutes = minutesDiff - (fullHours * 60);
    return `${fullHours}h ${remainingMinutes}m`;
  }

  if (minutesDiff === 1) {
    return '1 minute';
  }
  return `${minutesDiff} minutes`;
};

export const getDifferenceFromTimestampInSeconds = (timestamp) => {
  return Math.round((Date.now() - timestamp) / 1000);
};

export const getRelativeTimeFromTimestamp = (timestamp) => {
  const secondsDiff = getDifferenceFromTimestampInSeconds(timestamp);

  return formatRelativeTimeDifference(secondsDiff);
};

export const isDatePastOrUnavailable = (date, unavailableDates = [], advanceNoticeHours = 0) => {
  const dayJsDate = window.dayjs(date, 'DD/MM/YYYY');
  const dateStandardFormat = dayJsDate.format('YYYY-MM-DD');

  const dateBooked = unavailableDates.find(unavailableDate => unavailableDate === dateStandardFormat);
  if (dateBooked) {
    return true;
  }
  const compareTo = window.dayjs().add(advanceNoticeHours, 'hours');
  return dayJsDate.isBefore(compareTo, 'day');
};

export const isTimeUnavailable = (date, timeFrom, timeTo, roomAvailability, timeFormat) => {
  const bookedTimeRanges = extractBookedTimeRangesOfDate(date.value, roomAvailability.partiallyBookedDates);
  const selectedTimeRangeHasBookedHours = checkIfSelectedTimeTouchesBookedTimeRanges(
    bookedTimeRanges,
    timeFrom.value,
    timeTo.value,
  );
  const availableTimeOptions = getTimeOptions(
    date,
    roomAvailability,
    timeFormat,
  );
  const timeFromIsInOpeningHoursRange = availableTimeOptions.find(t => t.id === timeFrom.value);
  const timeToIsInOpeningHoursRange = availableTimeOptions.find(t => t.id === timeFrom.value);

  return selectedTimeRangeHasBookedHours || !timeFromIsInOpeningHoursRange || !timeToIsInOpeningHoursRange;
};

export function findWeekdayOccurrence(dateString) {
  // Parse the date using Day.js
  const date = window.dayjs(dateString, 'DD/MM/YYYY');

  // Find the weekday (0-6, Sunday to Saturday)
  const weekday = date.day();

  // Find the first day of the month
  const firstDayOfMonth = date.startOf('month');

  // Count how many times this weekday occurs in the month
  let count = 0;
  let occurrence = 0;
  for (let i = 0; i < 31; i += 1) {
    const newDate = firstDayOfMonth.add(i, 'day');

    // Break the loop if we go into the next month
    if (newDate.month() !== date.month()) break;

    // Check if this day is the same weekday
    if (newDate.day() === weekday) {
      count += 1;

      // Check if this is the date we are looking for
      if (newDate.isSame(date, 'day')) {
        occurrence = count;
      }
    }
  }

  // Determine if it's "first", "second", "third", or "last"
  if (occurrence === 1) return 'first';
  if (occurrence === 2) return 'second';
  if (occurrence === 3) return 'third';
  if (occurrence === count) return 'last';

  return 'fourth';
}
